import React, {useState, useEffect} from 'react';
import PropTypes from 'prop-types';
import {withRouter} from 'react-router';
import {resource} from '@albert-io/json-api-framework/request/builder';
import {
  Button,
  IconButton,
  Modal,
  Dialogue,
  TextField,
  SelectField,
  Form,
  Icon,
  LoadingSpinner,
  Text,
  Anchor,
  addToast
} from '@albert-io/atomic';
import masqueradeStore from 'generic/Masquerade/Masquerade.store';
import {getSchoolOptionsWithMasqueradeState} from 'generic/Masquerade/helpers';

import useAsyncFunctionStatus from 'lib/hooks/useAsyncFunctionStatus';

import GradeSelector from '../../shared/GradeSelector/GradeSelector.react';

import {
  validateForm,
  createClassroom,
  invalidateClassroomsQueries,
  formValidators
} from './newClassroomModal.utils';

export const AddClassButton = withRouter(function AddClassButton(props) {
  const buttonText = props.label || 'New class';
  const isDefaultOpen = Object.prototype.hasOwnProperty.call(
    props.location.query,
    'createClassroomModal'
  );
  const [isOpen, setIsOpen] = useState(props.handleDefaultOpen && isDefaultOpen);
  const toggleIsOpen = () => setIsOpen((currentIsOpen) => !currentIsOpen);
  return (
    <>
      <Button onClick={toggleIsOpen} variant={props.variant}>
        <Icon icon='plus' className='u-mar-r_1' />
        {buttonText}
      </Button>
      {isOpen && <NewClassroomModal handleClose={toggleIsOpen} />}
    </>
  );
});

export const getGradeLevelsQuery = () => {
  return resource('grade_levels_v1').mandarkEndpoint(['grade_levels_v1']);
};

export function NewClassroomModal({handleClose, onSubmit}) {
  const [formErrors, setFormErrors] = useState({});
  const [selectedGradeLevels, setSelectedGradeLevels] = useState([]);
  const [isReady, setIsReady] = useState(false);
  const [schoolOptions, setSchoolOptions] = useState([]);
  const [allGradeLevels, setAllGradeLevels] = useState([]);

  const {status: createClassroomStatus, callback: createClassroomCb} =
    useAsyncFunctionStatus(createClassroom);

  const isCreateClassroomPending = createClassroomStatus === 'pending';

  useEffect(() => {
    let mounted = true;
    async function fetchData() {
      try {
        Promise.all([
          getSchoolOptionsWithMasqueradeState(masqueradeStore.getUserIdByMasqueradeState()),
          getGradeLevelsQuery().getResourcePromise()
        ]).then(([returnedSchoolOptions, gradeLevels]) => {
          const sortedGradeLevels = gradeLevels.sort((a, b) => {
            return a.getGradeLevelType().localeCompare(b.getGradeLevelType(), undefined, {
              numeric: true,
              sensitivity: 'base'
            });
          });
          if (mounted) {
            setSchoolOptions(returnedSchoolOptions);
            setAllGradeLevels(sortedGradeLevels);
            setIsReady(true);
          }
        });
      } catch (error) {
        addToast({
          color: 'negative',
          title: 'Error',
          message: 'Something went wrong with your request. Please try again.'
        });
      }
    }
    fetchData();
    return () => {
      mounted = false;
    };
  }, []);

  const onFormSubmit = async (e) => {
    const name = e.target.name?.value;
    const schoolId = e.target.schoolId?.value;
    const validationErrors = validateForm({
      name,
      schoolId,
      gradeLevels: selectedGradeLevels
    });
    if (validationErrors) {
      setFormErrors(validationErrors);
      return;
    }
    createClassroomCb({
      teacherId: masqueradeStore.getUserIdByMasqueradeState(),
      schoolId,
      name,
      gradeLevelIds: selectedGradeLevels.map((gradeLevel) => gradeLevel.getId())
    })
      .then(invalidateClassroomsQueries)
      .then(() => {
        addToast({
          color: 'positive',
          title: 'Success!',
          message: `${name} created!`
        });
        if (typeof onSubmit === 'function') {
          onSubmit();
        }
      })
      .catch(() => {
        addToast({
          color: 'negative',
          title: 'Error',
          message: 'Something went wrong with your request. Please try again.'
        });
      })
      .finally(handleClose);
  };

  return (
    <Modal ariaLabel='Create new classroom modal' handleClose={handleClose}>
      {({CloseButtonWrapper, modalContentStyle}) => {
        return (
          <Dialogue inModal hideCloseBtn paddingLevel={0} className={modalContentStyle}>
            <Dialogue.Body>
              <div className='u-pad_2 u-font-weight_700 u-border-b u-border-color_slate-200 u-width_100pc u-display_flex u-justify-content_space-between u-align-items_center'>
                <Text as='div'>New Class</Text>
                <IconButton
                  icon='times'
                  size='s'
                  onClick={handleClose}
                  label='Close new class modal'
                />
              </div>
              <Form
                data-testid='new-classroom-form'
                onSubmit={(e) => {
                  onFormSubmit(e);
                }}
              >
                {!isReady ? (
                  <LoadingSpinner className='u-mar_2' size={3} />
                ) : (
                  <div className='u-pad_4'>
                    <TextField
                      data-testid='new-classroom-modal__class-name-field'
                      label='Class Name'
                      name='name'
                      message={formErrors.name}
                      error={!!formErrors.name}
                      disabled={isCreateClassroomPending}
                      className='u-mar-b_2'
                      onChange={(e) => {
                        const error = formValidators.name(e.target.value);
                        setFormErrors((currState) => ({
                          ...currState,
                          name: error
                        }));
                      }}
                    />
                    <GradeSelector
                      setSelectedGradeLevels={setSelectedGradeLevels}
                      selectedGradeLevels={selectedGradeLevels}
                      errorMessage={!!formErrors.gradeLevels && formErrors.gradeLevels}
                      allGradeLevels={allGradeLevels}
                    />
                    <SelectField
                      label='School Association'
                      name='schoolId'
                      data-testid='new-classroom-modal__school-association-dropdown'
                      message={formErrors.schoolId}
                      error={!!formErrors.schoolId}
                      placeholder='Choose...'
                      options={schoolOptions}
                      disabled={isCreateClassroomPending}
                      className='u-mar-b_2'
                      onChange={(e) => {
                        const error = formValidators.schoolId(e.target.value);
                        setFormErrors((currState) => ({
                          ...currState,
                          schoolId: error
                        }));
                      }}
                    />
                    <Text size='m' color='tertiary'>
                      Administrators at this school will have access to the class data. Contact{' '}
                      <Anchor underlined href='mailto:hello@albert.io'>
                        hello@albert.io
                      </Anchor>{' '}
                      if this is a problem.
                    </Text>
                  </div>
                )}
                <div className='u-pad_2 u-border-t u-border-color_slate-200 u-display_flex u-justify-content_flex-end'>
                  <CloseButtonWrapper>
                    <Button color='secondary' className='u-mar-r_1'>
                      Cancel
                    </Button>
                  </CloseButtonWrapper>
                  <Button
                    data-testid='create-class-button'
                    disabled={!isReady || isCreateClassroomPending}
                    type='submit'
                  >
                    Create class
                  </Button>
                </div>
              </Form>
            </Dialogue.Body>
          </Dialogue>
        );
      }}
    </Modal>
  );
}

NewClassroomModal.propTypes = {
  handleClose: PropTypes.func,
  onSubmit: PropTypes.func
};
